import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../hooks/useConfig';
import { useWriteGate } from '../lib/writeGate';
import { useProfileContext } from '../hooks/useProfileContext';
import { apiGetPdfLayoutBand, apiGetPdfLayoutEffective, apiSavePdfLayoutBand } from '../api';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import type { GeneralConfig, TransportType } from '../types';
import ConfigPageHeader from '../components/ConfigPageHeader';
import ConfigField from '../components/ConfigField';
import AutoTextarea from '../components/AutoTextarea';
import PopulateFrom from '../components/PopulateFrom';
import { useFocusField } from '../hooks/useFocusField';

/** U2i — the ceiling of every "… minutes" field of this page. The user's rule:
 * we will never go past 999 minutes. Enforced client-side (input `max` + a
 * refused onChange). The server stores `rider_config` as a JSON blob and does
 * NOT validate its values, so no server check exists to mirror — measured, not
 * assumed (see the handoff + `ui-densite-2026.md` §17). */
export const MINUTES_MAX = 999;

/** The four General fields that also appear in the Rider Layout. */
export interface GeneralLayoutSection {
  key: keyof GeneralConfig;
  fieldId: string;
  title: string;
  label: string;
  placeholder: string;
  /** Show & Setup fields are rendered as number inputs with a note. */
  type?: 'text' | 'number';
  note?: string;
  /** U2i — ROW GROUP. Fields sharing the same group render side by side, one
   * short cell each, inside a SINGLE row (five 2-digit numbers otherwise ate a
   * full-width row each — ~700 px). Reusable: a future short-field group only
   * declares the same tag. A grouped field keeps its own label, its own
   * buttons, its own `data-field-id` (layout deep-links) and its own order. */
  rowGroup?: 'times';
}

export const GENERAL_LAYOUT_SECTIONS: GeneralLayoutSection[] = [
  { key: 'duration', fieldId: 'general-info.show-duration', title: 'configGeneral.duration', label: 'configGeneral.durationLabel', type: 'number', note: 'configGeneral.durationNote', placeholder: '', rowGroup: 'times' },
  { key: 'installationTimeMinutes', fieldId: 'general-info.installation', title: 'configGeneral.installation', label: 'configGeneral.installationLabel', type: 'number', placeholder: '', rowGroup: 'times' },
  { key: 'soundcheckDurationMinutes', fieldId: 'general-info.soundcheck', title: 'configGeneral.soundcheck', label: 'configGeneral.soundcheckLabel', type: 'number', placeholder: '', rowGroup: 'times' },
  { key: 'changeoverDurationMinutes', fieldId: 'general-info.changeover', title: 'configGeneral.changeover', label: 'configGeneral.changeoverLabel', type: 'number', placeholder: '', rowGroup: 'times' },
  { key: 'breakdownDurationMinutes', fieldId: 'general-info.breakdown', title: 'configGeneral.breakdown', label: 'configGeneral.breakdownLabel', type: 'number', placeholder: '', rowGroup: 'times' },
  { key: 'security', fieldId: 'general-info.security', title: 'configGeneral.security', label: 'configGeneral.security', placeholder: 'e.g. Stage security 1-2 people, liability & equipment insurance, safety briefing at load-in' },
  { key: 'merchandise', fieldId: 'general-info.merchandise', title: 'configGeneral.merchandising', label: 'configGeneral.merchandising', placeholder: 'e.g. 1 merchandise table with lights, no percentage to venue, power outlet access' },
  { key: 'specialRequests', fieldId: 'general-info.special-requests', title: 'configGeneral.localCrew', label: 'configGeneral.localCrewLabel', placeholder: 'e.g. 1 sound tech + 1 lighting tech from get-in to departure, 1 stagehand for load-in/out' },
  { key: 'notes', fieldId: 'general-info.notes', title: 'configGeneral.notes', label: 'configGeneral.notes', placeholder: 'e.g. Parking / load-in directions, special requests, backline change notes' },
];

/** Order the General config sections to mirror the Rider Layout field order
 * (general-info section). Fields absent from the layout keep their default
 * relative order at the end. */
export function sortGeneralSections(layoutOrder: string[] | null): GeneralLayoutSection[] {
  if (!layoutOrder) return GENERAL_LAYOUT_SECTIONS;
  const rank = new Map(layoutOrder.map((id, i) => [id, i]));
  return [...GENERAL_LAYOUT_SECTIONS].sort((a, b) => {
    const ra = rank.get(a.fieldId);
    const rb = rank.get(b.fieldId);
    if (ra !== undefined && rb !== undefined) return ra - rb;
    if (ra !== undefined) return -1;
    if (rb !== undefined) return 1;
    return 0;
  });
}

export default function ConfigGeneralPage() {
  const { config, updateGeneral } = useConfig();

  // Capability-aware write gate (lot A2-CAP, 2026-09-25). Every write of this
  // page persists through `useConfig` -> PUT /api/bands/:id/rider/config, the
  // rider CONTENT: a globally `read-only` account keeps it ONLY through
  // `content.rider` (conditional pre-guard mount, server/index.js). NEVER the
  // blanket `canWrite` here — it would freeze the very crew member the
  // capability is for.
  const writeGate = useWriteGate();
  const canEditContent = writeGate.canWriteCap('content.rider');
  // The two INTRO texts of this page are NOT rider config: they persist into the
  // band's PDF layout override (`PUT /api/bands/:id/pdf-layout`), which the
  // server delegates to `layout.rider` (mount AHEAD of the guard, router's own
  // canWriteLayout(...,'rider')). A holder of `content.rider` alone would get a
  // 403 there, so those two fields follow THEIR family's capability.
  const canEditIntro = writeGate.canWriteCap('layout.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canEditContent;
  const { t } = useTranslation();
  const { activeProfileId } = useProfileContext();
  const [transport, setTransport] = useState<TransportType>('van');
  // Legal intro sentence — lives in the PDF layout (band override), NOT in the
  // general config. Loaded here so it can be edited in the band settings too.
  // Autosaved (debounced) like the rest of the page, no Save button. (2026-08-27)
  const [introText, setIntroText] = useState('');
  const [introLoaded, setIntroLoaded] = useState(false);
  const [introSaved, setIntroSaved] = useState(false);
  // Technical intro (v0.591) — own TECHNICAL RIDER section in the rider, above
  // FOH & MON. Band-level layout text (texts.techIntroText) like the legal intro
  // above; while the band has no such key, the legacy SoundConfig.intro value is
  // shown here (and still printed) so nothing is lost before the first edit.
  const [techIntro, setTechIntro] = useState('');
  const [techIntroSaved, setTechIntroSaved] = useState(false);
  const lastTechIntroSavedRef = useRef('');
  // Anti-wipe: last value we loaded/saved; we never autosave an unloaded or
  // unchanged value over the real data (a transient 401/500 must not wipe it).
  const lastIntroSavedRef = useRef('');
  // Deep-link focus (2026-08-11): the Layout Editor links with ?focus=<fieldId>.
  useFocusField(true);
  // Field order from the Rider Layout (effective = global + band override):
  // the config page mirrors the layout's section order for the fields it hosts.
  const [layoutOrder, setLayoutOrder] = useState<string[] | null>(null);

  useEffect(() => {
    if (!activeProfileId) { setIntroLoaded(true); return; }
    let cancelled = false;
    Promise.all([
      apiGetPdfLayoutBand(activeProfileId).catch(() => null),
      apiGetPdfLayoutEffective(activeProfileId).catch(() => null),
    ]).then(([band, effective]) => {
      if (cancelled) return;
      setIntroText(band?.texts?.introText ?? '');
      lastIntroSavedRef.current = band?.texts?.introText ?? '';
      // Effective technical intro: the band's layout key if it exists, else the
      // legacy per-transport SoundConfig.intro (read-only fallback, never
      // written until the user edits → opening the page writes nothing).
      const layoutTech = band?.texts?.techIntroText;
      const legacyTech = (config.sound?.[transport]?.intro as string) || '';
      const effectiveTech = (layoutTech === undefined ? legacyTech : layoutTech) || '';
      setTechIntro(effectiveTech);
      lastTechIntroSavedRef.current = effectiveTech;
      const gi = (effective?.sections || []).find((s: any) => s.id === 'general-info');
      if (gi && Array.isArray(gi.fields) && gi.fields.length > 0) {
        setLayoutOrder(gi.fields.map((f: any) => f.id));
      }
      setIntroLoaded(true);
    }).catch(() => { if (!cancelled) setIntroLoaded(true); });
    return () => { cancelled = true; };
  }, [activeProfileId]);

  // Autosave the intro sentence (debounced) — same pattern as TeamEditPage.
  // Persists into the band's PDF layout override, no Save button. (2026-08-27)
  useEffect(() => {
    if (!canEditIntro) return; // no `layout.rider` → the server answers 403
    if (!introLoaded) return; // never autosave before load (anti-wipe)
    if (introText === lastIntroSavedRef.current) return; // unchanged
    const timer = setTimeout(async () => {
      try {
        const existing = (await apiGetPdfLayoutBand(activeProfileId!).catch(() => null)) ?? {};
        const next = { ...existing, texts: { ...(existing.texts || {}), introText } };
        await apiSavePdfLayoutBand(activeProfileId!, next);
        lastIntroSavedRef.current = introText;
        setIntroSaved(true);
        reportSaveOk('config');
      } catch (err) {
        reportSaveError('config', err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [introText, introLoaded, activeProfileId]);

  // Autosave the technical intro (debounced), same anti-wipe pattern.
  useEffect(() => {
    if (!canEditIntro) return; // same `layout.rider` rule as the legal intro
    if (!introLoaded) return;
    if (techIntro === lastTechIntroSavedRef.current) return;
    const timer = setTimeout(async () => {
      try {
        const existing = (await apiGetPdfLayoutBand(activeProfileId!).catch(() => null)) ?? {};
        const next = { ...existing, texts: { ...(existing.texts || {}), techIntroText: techIntro } };
        await apiSavePdfLayoutBand(activeProfileId!, next);
        lastTechIntroSavedRef.current = techIntro;
        setTechIntroSaved(true);
        reportSaveOk('config');
      } catch (err) {
        reportSaveError('config', err);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [techIntro, introLoaded, activeProfileId]);

  const cfg = config.general[transport];

  const handleChange = (key: keyof GeneralConfig, value: any) => {
    updateGeneral({
      [transport]: { ...cfg, [key]: value },
    } as any);
  };

  const populateAll = async (src: TransportType) => {
    const srcCfg = config.general[src];
    if (!(await showConfirm(t('configGeneral.populateAllConfirm', { src })))) return;
    updateGeneral({ [transport]: { ...srcCfg } } as any);
  };

  const populateField = (key: keyof GeneralConfig) => async (src: TransportType) => {
    const srcVal = config.general[src][key];
    if (srcVal === cfg[key]) return;
    if (await showConfirm(`Overwrite "${key}" with the value from ${src}?`)) {
      handleChange(key, srcVal);
    }
  };

  // The four General fields that also appear in the Rider Layout (general-info
  // section) are rendered in the SAME order as the layout, so reordering in
  // Rider Layout is reflected here. Default order (no layout saved) is the
  // engine's default field order: security, merchandise, special-requests, notes.
  const generalSections = useMemo(() => sortGeneralSections(layoutOrder), [layoutOrder]);

  // U2i — ROW GROUP. The five "… minutes" fields (and any future field that
  // declares `rowGroup`) collapse into ONE row of short cells, placed where the
  // FIRST of them sits in the layout-driven order. Every other field keeps its
  // own full-width section, untouched. No label, no value, no i18n key and no
  // field order changes: the cells are rendered in the same relative order.
  const timesSections = useMemo(() => generalSections.filter((s) => s.rowGroup === 'times'), [generalSections]);
  const renderItems = useMemo(() => {
    const items: Array<{ kind: 'field'; section: GeneralLayoutSection } | { kind: 'times' }> = [];
    let placed = false;
    for (const s of generalSections) {
      if (s.rowGroup === 'times') {
        if (!placed) { items.push({ kind: 'times' }); placed = true; }
        continue;
      }
      items.push({ kind: 'field', section: s });
    }
    return items;
  }, [generalSections]);
  const timesNote = timesSections.find((s) => s.note)?.note;

  // U2i — REFUSE anything above MINUTES_MAX, never clamp MID-TYPING (clamping in
  // an onChange breaks free entry — the documented "90 becomes 40" trap). A 4th
  // digit ("1000") simply does not reach the state, so the field snaps back.
  const handleMinutesChange = (key: keyof GeneralConfig, raw: string) => {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) { handleChange(key, 0); return; }
    if (n < 0 || n > MINUTES_MAX) return;
    handleChange(key, n);
  };

  return (
    <div className="page config-page" data-config-page="general" data-density="socle">
      <ConfigPageHeader disabled={!canEditContent}
        iconKey="configGeneral.title"
        title={t('configGeneral.title')}
        transport={transport}
        onTransportChange={setTransport}
        onPopulateAll={populateAll}
      />

      {readOnlyNote && (
        <p className="text-dim" data-readonly-note style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>{t('common.readOnlyAccount')}</p>
      )}
      <div className="edit-form">
        <div className="form-section" data-field-id="intro.intro">
          <h2>{t('configGeneral.rider')}</h2>
          <ConfigField disabled={!canEditIntro} label={t('configGeneral.riderIntro')} current={transport} labelBlock>
            {!introLoaded ? (
              <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>
            ) : (
              <>
                <AutoTextarea disabled={!canEditIntro}
                  value={introText}
                  onChange={(e) => { setIntroText(e.target.value); setIntroSaved(false); }}
                  placeholder={t('configGeneral.introPh')}
                  rows={3}
                />
                {/* U2f — l'écart AFFICHÉ sous le contrôle vient de la rangée
                    (`--ui-gap-field`), plus d'un margin local : 8px au-dessus
                    comme en dessous. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {introSaved && <span style={{ color: 'var(--success)', fontSize: '0.85rem' }}>{t('configGeneral.saved')}</span>}
                  <small style={{ color: 'var(--text-dim)' }}>
                    {t('configGeneral.introHint')}
                  </small>
                </div>
              </>
            )}
          </ConfigField>
        </div>

        {/* Technical intro (v0.591) — its own TECHNICAL RIDER section in the rider,
            placed just above FOH & MON. Edited here (band-level, like the legal
            intro above) instead of on the Sound/FOH & MON config page. */}
        <div className="form-section" data-field-id="technical-rider.intro">
          <h2>{t('configGeneral.techIntro')}</h2>
          <ConfigField disabled={!canEditIntro} label={t('configGeneral.techIntroLabel')} current={transport} labelBlock>
            {!introLoaded ? (
              <p style={{ color: 'var(--text-dim)' }}>{t('common.loading')}</p>
            ) : (
              <>
                <AutoTextarea disabled={!canEditIntro}
                  value={techIntro}
                  onChange={(e) => { setTechIntro(e.target.value); setTechIntroSaved(false); }}
                  placeholder={t('configGeneral.techIntroPh')}
                  rows={4}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {techIntroSaved && <span style={{ color: 'var(--success)', fontSize: '0.85rem' }}>{t('configGeneral.saved')}</span>}
                  <small style={{ color: 'var(--text-dim)' }}>
                    {t('configGeneral.techIntroHint')}
                  </small>
                </div>
              </>
            )}
          </ConfigField>
        </div>

        <div className="form-section">
          <h2>{t('configGeneral.riderVersion')}</h2>
          <ConfigField disabled={!canEditContent} label={t('configGeneral.versionLabel')} current={transport} exampleText="v30.04.26" onUseExample={(t) => handleChange('version', t)} onPopulateFrom={populateField('version')}>
            <AutoTextarea disabled={!canEditContent}
              value={cfg.version || ''}
              onChange={(e) => handleChange('version', e.target.value)}
              placeholder="e.g. v30.04.26"
            />
          </ConfigField>
        </div>

        {/* Rehearsal & Setup was MOVED to the Sound page (2026-08-08) — the
            fields still live on this General config under the hood. */}
        {renderItems.map((item) => {
          if (item.kind === 'times') {
            return (
              <div className="form-section" key="general-times">
                {/* U2i — en-tête de la rangée groupée. Même classe que les titres
                    de SOUS-section (`.rc-section-title`, 12,48 px) et non un
                    `h4` nu à 13,5 px : ce dernier serait PLUS GROS que le titre
                    de section de la page (`h2` de config, 12,8 px) et casserait
                    l'échelle décroissante page > carte > sous-carte > section.
                    Clé i18n propre (« Durations » / « Durées » / « Duraciones ») :
                    « timings » désignerait les HORAIRES de la journée, pas des
                    durées en minutes. */}
                <h4 className="rc-section-title config-times-title">{t('configGeneral.durationsTitle')}</h4>
                <div className="form-row config-times-row" data-times-row>
                  {timesSections.map((s) => (
                    <div className="config-times-cell" key={s.key} data-field-id={s.fieldId}>
                      <label className="config-field-label" htmlFor={`general-times-${s.key}`}>
                        <span>{t(s.label)}</span>
                        <span className="config-field-actions">
                          <PopulateFrom current={transport} compact onSelect={populateField(s.key)} disabled={!canEditContent} />
                        </span>
                      </label>
                      <div className="config-times-ctl">
                        <input
                          id={`general-times-${s.key}`}
                          type="number"
                          inputMode="numeric"
                          min={0}
                          max={MINUTES_MAX}
                          step={1}
                          value={cfg[s.key] || 0}
                          onChange={(e) => handleMinutesChange(s.key, e.target.value)}
                          disabled={!canEditContent}
                        />
                        {/* Unit SYMBOL, not copy: "min" is identical in EN/FR/ES
                            and the label already carries the word "minutes". */}
                        <span className="config-times-unit" aria-hidden="true">min</span>
                      </div>
                    </div>
                  ))}
                </div>
                {timesNote && (
                  <p className="config-times-note" style={{ color: 'var(--text-dim)' }}>{t(timesNote)}</p>
                )}
              </div>
            );
          }
          const s = item.section;
          return (
            <div className="form-section" key={s.key} data-field-id={s.fieldId}>
              <h2>{t(s.title)}</h2>
              <ConfigField disabled={!canEditContent}
                label={t(s.label)}
                current={transport}
                onPopulateFrom={populateField(s.key)}
                labelBlock={s.type === 'text' || !s.type}
                exampleText={s.placeholder?.startsWith('e.g. ') ? s.placeholder.replace(/^e\.g\.\s+/, '') : undefined}
                onUseExample={s.placeholder?.startsWith('e.g. ') ? (t) => handleChange(s.key, t) : undefined}
              >
                <AutoTextarea disabled={!canEditContent}
                 value={String(cfg[s.key] ?? '')}
                 onChange={(e) => handleChange(s.key, e.target.value)}
                 placeholder={s.placeholder}
                 rows={3}
                />
              </ConfigField>
              {s.note && (
                <p style={{ margin: '-6px 0 12px', color: 'var(--text-dim)', fontSize: '0.8rem' }}>
                  {t(s.note)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
